/* DigiGuide Import / Export – Home / Selector page (UPDATED 2025‑06‑17‑d)
   -----------------------------------------------------------------------------
   ● Typing lag gone (uncontrolled <Input>)
   ● Set‑Token button live‑enables via hasText
   ● Fatal‑token clearing now waits for the **new** query to finish – solves the
     loop where a *valid* token was being cleared because the old error still
     lingered in Apollo’s cache.
*/

import * as React from "react";
import { useLocation, navigate } from "@reach/router";
import { useQuery } from "@apollo/client";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Input from "@mui/material/Input";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { Text } from "@base";
import importClient from "../apollo/importClient";
import ManualSelector from "../components/importexport/ManualSelector";
import ImportHeader from "../components/importexport/ImportHeader";
import QUERY_MANUAL_LIST_ALL_SITES from "../craft/dynamicQueries/QUERY_MANUAL_LIST_ALL_SITES";

const SELECT_PATH = "/importexport/select";

/**
 * Form for entering AWS S3 credentials used by the per-manual "upload to S3"
 * buttons. Values persist in localStorage under the same keys the URL params
 * use (S3_API_KEY, S3_SECRET, S3_REGION, S3_BUCKET) so the existing handlers
 * can prefer URL → localStorage with no further changes to the URL flow.
 */
const S3_FIELDS = [
  { key: "S3_API_KEY", label: "AWS Access Key ID" },
  { key: "S3_SECRET", label: "AWS Secret Access Key", secret: true },
  { key: "S3_REGION", label: "AWS Region", placeholder: "us-west-2" },
  { key: "S3_BUCKET", label: "S3 Bucket" },
];

function S3CredentialsForm() {
  const [values, setValues] = React.useState(() =>
    S3_FIELDS.reduce((acc, f) => {
      acc[f.key] =
        (typeof window !== "undefined" && localStorage.getItem(f.key)) || "";
      return acc;
    }, {})
  );

  const update = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
    if (val) localStorage.setItem(key, val);
    else localStorage.removeItem(key);
  };

  return (
    <Paper elevation={2} sx={{ p: 4, maxWidth: 720, mx: "auto", mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        S3 Upload Credentials
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Required for the "➜ S3" buttons on manual pages. Stored in this
        browser only. URL params override these values.
      </Typography>
      <Stack spacing={2}>
        {S3_FIELDS.map(f => (
          <TextField
            key={f.key}
            label={f.label}
            type={f.secret ? "password" : "text"}
            placeholder={f.placeholder}
            value={values[f.key]}
            onChange={e => update(f.key, e.target.value)}
            fullWidth
            size="small"
          />
        ))}
      </Stack>
    </Paper>
  );
}

export default function ImportExportHome() {
  const location = useLocation();
  const qsToken =
    new URLSearchParams(location.search).get("CRAFT_API_IMPORTEXPORT_TOKEN") ||
    new URLSearchParams(location.search).get("token") ||
    new URLSearchParams(location.search).get("x-craft-live-preview") ||
    null;
  const stored =
    typeof window !== "undefined"
      ? localStorage.getItem("GATSBY_CRAFT_API_EXPORT_TOKEN") || ""
      : "";

  const tokenRef = React.useRef(null);
  const [hasAuth, setAuth] = React.useState(Boolean(stored || qsToken));
  const [hasText, setHasText] = React.useState(Boolean(stored || qsToken));
  const [fatalErr, setFatal] = React.useState(null);

  /* ------------------------------------------------------------------ */
  /*  bootstrap from query‑string token                                 */
  /* ------------------------------------------------------------------ */
  React.useEffect(() => {
    if (qsToken) {
      localStorage.setItem("GATSBY_CRAFT_API_EXPORT_TOKEN", qsToken);
      setAuth(true);
      navigate(SELECT_PATH, { replace: true });
    }
  }, [qsToken]);

  /* redirect to /select if we already have a token */
  React.useEffect(() => {
    if (hasAuth && location.pathname === "/importexport") {
      navigate(SELECT_PATH, { replace: true });
    }
  }, [hasAuth, location.pathname]);

  /* Apollo query – skipped until hasAuth === true */
  const { data, loading, error } = useQuery(QUERY_MANUAL_LIST_ALL_SITES, {
    client: importClient,
    skip: !hasAuth,
    fetchPolicy: "cache-and-network",
  });

  /* ------------------------------------------------------------------ */
  /*  fatal token detector – *after* the latest request finishes         */
  /* ------------------------------------------------------------------ */
  React.useEffect(() => {
    if (loading || !error) return; // <-- guard 👈

    if (/getNullableType/i.test(error.message)) {
      setFatal(error);
      localStorage.removeItem("GATSBY_CRAFT_API_EXPORT_TOKEN");
      setAuth(false);
      if (tokenRef.current) tokenRef.current.value = "";
      setHasText(false);
      navigate("/importexport", { replace: true });
    }
  }, [error, loading]); // add loading dep 👈

  /* ------------------------------------------------------------------ */
  /*  helpers                                                           */
  /* ------------------------------------------------------------------ */
  function saveToken() {
    const val = tokenRef.current?.value?.trim();
    if (!val) return;
    localStorage.setItem("GATSBY_CRAFT_API_EXPORT_TOKEN", val);
    setAuth(true);
    navigate(SELECT_PATH);
  }

  const Backdrop = ({ children }) => (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>{children}</Box>
  );

  /* ---------------- AUTH FORM ---------------- */
  if (!hasAuth) {
    return (
      <Backdrop>
        <ImportHeader
          title="DigiGuide Advanced Import & Export"
          subHeader="Enter Authentication Token"
        />
        <Paper elevation={2} sx={{ p: 4, maxWidth: 480, mx: "auto" }}>
          {fatalErr && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "#ffebee",
                border: "1px solid #f44336",
                borderRadius: 1,
                color: "#c62828",
                fontSize: 14,
              }}
            >
              <strong>Failed to load manuals:</strong>
              <br />
              {fatalErr.message}
            </Box>
          )}
          <Stack spacing={3}>
            <Text tag="p">
              Enter a Craft API token with <b>read&nbsp;+&nbsp;write</b> scope.
            </Text>

            <Input
              inputRef={tokenRef}
              autoFocus
              fullWidth
              placeholder="Craft auth token"
              defaultValue={stored || qsToken}
              onChange={e => setHasText(Boolean(e.target.value.trim()))}
            />

            <Button variant="contained" onClick={saveToken} disabled={!hasText}>
              Set Token
            </Button>
          </Stack>
        </Paper>
      </Backdrop>
    );
  }

  /* ---------------- LOADING ---------------- */
  if (loading) {
    return (
      <Backdrop>
        <ImportHeader title="DigiGuide Import / Export" subHeader="Loading…" />
        <Paper
          elevation={2}
          sx={{ p: 4, maxWidth: 480, mx: "auto", textAlign: "center" }}
        >
          <CircularProgress size={48} />
        </Paper>
      </Backdrop>
    );
  }

  /* ---------------- non‑fatal error ---------------- */
  if (error) {
    return (
      <Backdrop>
        <ImportHeader title="DigiGuide Import / Export" subHeader="Error" />
        <Paper elevation={2} sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
          <Text
            tag="p"
            use="span"
            options={{ size: "text-lg" }}
            style={{ color: "red" }}
          >
            Failed to load manuals: {error.message}
          </Text>
        </Paper>
      </Backdrop>
    );
  }

  /* ---------------- SELECTOR ---------------- */
  const manuals = data?.products ?? [];

  return (
    <Backdrop>
      <ImportHeader
        title="DigiGuide Advanced Import & Export"
        subHeader="Select a Manual & Language"
      />
      <Paper elevation={2} sx={{ p: 4, maxWidth: 720, mx: "auto" }}>
        <ManualSelector
          manuals={manuals}
          prevPosition={localStorage.getItem("PREV_POSITION")}
        />
      </Paper>
      <S3CredentialsForm />
    </Backdrop>
  );
}
